import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

/** Compose class names, letting later Tailwind utilities win over earlier ones. */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * Convert a hex colour to the `H S% L%` triplet Tailwind's theme expects.
 *
 * The design tokens are stored as hex because that is what a colour picker in the admin
 * panel produces, but they are consumed as HSL channels so that Tailwind can apply
 * opacity modifiers (`bg-primary/10`) to them.
 *
 * Returns null for anything unparseable, so a malformed token is skipped rather than
 * writing `NaN` into the stylesheet.
 */
export function hexToHslChannels(hex: string): string | null {
  const match = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!match?.[1]) return null;

  let value = match[1];
  if (value.length === 3) {
    value = value
      .split('')
      .map((char) => char + char)
      .join('');
  }

  const r = Number.parseInt(value.slice(0, 2), 16) / 255;
  const g = Number.parseInt(value.slice(2, 4), 16) / 255;
  const b = Number.parseInt(value.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const lightness = (max + min) / 2;
  const delta = max - min;

  let hue = 0;
  let saturation = 0;

  if (delta !== 0) {
    saturation = delta / (1 - Math.abs(2 * lightness - 1));

    if (max === r) hue = ((g - b) / delta) % 6;
    else if (max === g) hue = (b - r) / delta + 2;
    else hue = (r - g) / delta + 4;

    hue *= 60;
    if (hue < 0) hue += 360;
  }

  return `${Math.round(hue)} ${Math.round(saturation * 100)}% ${Math.round(lightness * 100)}%`;
}

/** Format an ISO timestamp for display in the tenant's locale. */
export function formatDateTime(value: string | null | undefined, locale = 'en-GB'): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

/**
 * Terms that must not be sentence-cased.
 *
 * Without this, `humanise('rbac')` renders "Rbac" in the navigation and on the
 * dashboard — which reads as a typo rather than as an initialism.
 */
const ACRONYMS = new Map([
  ['rbac', 'RBAC'],
  ['sso', 'SSO'],
  ['api', 'API'],
  ['ip', 'IP'],
  ['url', 'URL'],
  ['id', 'ID'],
  ['sms', 'SMS'],
  ['pdf', 'PDF'],
  ['csv', 'CSV'],
  ['ldap', 'LDAP'],
  ['erp', 'ERP'],
  ['hrms', 'HRMS'],
]);

/** Turn a permission or role code into something readable: `role.create` → `Role create`. */
export function humanise(code: string): string {
  const words = code.replace(/[._-]/g, ' ').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '';

  const expanded = words.map((word) => ACRONYMS.get(word.toLowerCase()) ?? word);
  const [first, ...rest] = expanded;

  // Only the first word is capitalised; the rest stay as written, so
  // `user.status.change` reads "User status change" rather than title case.
  const lead = ACRONYMS.has(words[0]!.toLowerCase())
    ? first!
    : first!.charAt(0).toUpperCase() + first!.slice(1);

  return [lead, ...rest].join(' ');
}

/** Format an ISO timestamp as a date only, for columns where the time is noise. */
export function formatDate(value: string | null | undefined, locale = 'en-GB'): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(date);
}

const RELATIVE_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 24 * 60 * 60 * 1000],
  ['month', 30 * 24 * 60 * 60 * 1000],
  ['week', 7 * 24 * 60 * 60 * 1000],
  ['day', 24 * 60 * 60 * 1000],
  ['hour', 60 * 60 * 1000],
  ['minute', 60 * 1000],
];

/**
 * Format an ISO timestamp as "3 hours ago".
 *
 * Activity feeds are read for recency, not for the exact instant — the precise
 * timestamp belongs in a `title` attribute, not in the cell.
 */
export function formatRelativeTime(value: string | null | undefined, locale = 'en-GB'): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  const elapsed = date.getTime() - Date.now();
  const formatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

  for (const [unit, ms] of RELATIVE_UNITS) {
    if (Math.abs(elapsed) >= ms) {
      return formatter.format(Math.round(elapsed / ms), unit);
    }
  }

  return formatter.format(Math.round(elapsed / 1000), 'second');
}

/** Group separators for counters and table totals. */
export function formatNumber(value: number, locale = 'en-GB'): string {
  return new Intl.NumberFormat(locale).format(value);
}

/**
 * Up to two initials for an avatar fallback.
 *
 * Falls back through full name → email local part → '?', because a user invited
 * but not yet onboarded has an address and nothing else.
 */
export function initials(name: string | null | undefined, email?: string | null): string {
  const source = name?.trim() || email?.split('@')[0]?.replace(/[._-]+/g, ' ') || '';
  const parts = source.split(/\s+/).filter(Boolean);

  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();

  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

/**
 * The name to address the signed-in user by.
 *
 * Prefers a real name — full name, then first name — and only when the account carries
 * no name at all (invited but not yet onboarded) falls back to a humanised form of the
 * email's local part. The address itself is never returned: `admin@demo.localhost`
 * becomes "Admin", `john_doe@…` becomes "John Doe". Where the raw address is genuinely
 * needed — Profile, Account Settings — read `user.email` directly instead.
 */
export function userDisplayName(
  user:
    | { full_name?: string | null; first_name?: string | null; email?: string | null }
    | null
    | undefined,
): string {
  if (!user) return '';

  // The backend's `full_name` falls back to the email address when the account carries
  // no first or last name, so a candidate containing `@` is an address, not a name —
  // it is treated as absent and the local part is humanised below instead.
  const name = [user.full_name, user.first_name]
    .map((value) => value?.trim())
    .find((value) => value && !value.includes('@'));
  if (name) return name;

  const local = user.email?.split('@')[0]?.trim();
  if (!local) return '';

  // Split on the separators that stand in for spaces in a local part and title-case
  // each token, so a run like `john_doe` reads as a name rather than a login. A
  // separator-free run (`amitkumar`) can only have its first letter raised — there is
  // no dictionary here to guess word boundaries — which is still better than an address.
  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Pick a deterministic accent from the chart ramp for a given key.
 *
 * Deterministic so a user's avatar colour is stable across pages and sessions —
 * a random colour per render would make the same person look like two people.
 */
export function accentIndex(key: string, buckets = 6): number {
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  }
  return hash % buckets;
}

/** Split a string around the first case-insensitive match, for search highlighting. */
export function splitOnMatch(text: string, query: string): [string, string, string] | null {
  if (!query.trim()) return null;
  const index = text.toLowerCase().indexOf(query.trim().toLowerCase());
  if (index < 0) return null;

  return [text.slice(0, index), text.slice(index, index + query.trim().length), text.slice(index + query.trim().length)];
}
